// vendor libs
import context from 'app/common/lib/context';
import Facebook from 'app/common/lib/Facebook';
import pick from 'lodash/pick';
import ResourceFactory from 'app/common/lib/ResourceFactory';
import urlUtil from 'app/common/lib/urlUtil';
import user from 'app/common/lib/User';

const FB_REF_UNIQUE_ID_LENGTH = 20;

/**
 * @type number
 * const
 */
const FB_SHARER_WIDTH = 580;

/**
 * @type number
 * const
 */
const FB_SHARER_HEIGHT = 400;

function getMessengerIframe() {
  // NOTE: these are stackable so we should grab the latest and clear the others
  const messengerIframes = Array.from(document.querySelectorAll('.fb_dialog_iframe iframe'));

  if (!messengerIframes.length) {
    return undefined;
  }

  if (messengerIframes.length === 1) {
    return messengerIframes[0];
  }

  const deletable = messengerIframes.splice(0, messengerIframes.length - 1);

  for (let i = 0; i < deletable.length; i += 1) {
    deletable[i].parentNode.removeChild(deletable[i]);
  }

  return messengerIframes[0];
}

const FacebookDialog = {
  /**
   * Open a Facebook dialog.
   * See https://developers.facebook.com/docs/sharing/web for usage.
   * Also http://fbdevwiki.com/wiki/FB.ui for additional docs on FB.ui
   *
   * @param {{link:(undefined|string),
   *          name:(undefined|string),
   *          picture:(undefined|string),
   *          description:(undefined|string),
   *          type:(undefined|string),
   *          object_type:(undefined|string),
   *          object_id:(undefined|string),
   *          callback:(undefined|function(Object))}} options
   *  link The link to send in the message
   *  name The link text
   *  picture The picture shown next to the link
   *  description The description shown below the link
   *  type The type of FB dialog. Default: feed
   */
  open(options) {
    const params = pick(options, FacebookDialog._validParams);
    const type = FacebookDialog._getDialogType(options);
    const fbref = FacebookDialog.generateFbRef();
    const trackFeedShare = options.object_type && options.object_id && type === 'feed';

    let link = urlUtil.absolutify(options.path);
    const experiment_url_params = urlUtil.getQueryStringParams();
    if (experiment_url_params.ptrf) {
      link = urlUtil.appendQueryString(link, { ptrf: experiment_url_params.ptrf });
    }
    if (trackFeedShare) {
      link = urlUtil.appendQueryString(link, { fb_ref: fbref });
    }

    params.method = type;

    if (params.method === 'feed') {
      // https://developers.facebook.com/docs/sharing/reference/feed-dialog
      const windowOptions = FacebookDialog.getWindowOptions(
        options.width || FB_SHARER_WIDTH,
        options.height || FB_SHARER_HEIGHT,
      );

      let feedLink = 'http://www.facebook.com/sharer/sharer.php?u=' + link;

      if (params.picture) {
        feedLink += '&picture=' + params.picture;
      }
      if (params.name) {
        feedLink += '&title=' + params.name;
      }
      if (params.description) {
        feedLink += '&description=' + params.description;
      }
      if (params.caption) {
        feedLink += '&caption=' + params.caption;
      }

      window.open(encodeURI(feedLink), 'sharer', windowOptions);
    } else {
      if (params.method === 'send') {
        params.link = link;
      } else if (params.method === 'share') {
        params.href = link;
      }

      Facebook.ensureInit((_fb) => {
        _fb.getLoginStatus(() => {
          // Waiting for getLoginStatus's callback to fire guarantees that the
          // Facebook app has initialized with a user (if one exists). This means
          // fb.ui can take advantage of display methods (such as iframe) that
          // require a logged in user.

          // NOTE(mwalker): send method doesn't support callbacks, so we'll fabricate one once the UI is available
          // https://developers.facebook.com/docs/sharing/reference/send-dialog#response
          if (params.method === 'send') {
            _fb.ui(params);
            let tries = 10;
            let iframe;
            const interval = setInterval(() => {
              iframe = getMessengerIframe();
              if (iframe || !tries) {
                clearInterval(interval);
                if (iframe) {
                  FacebookDialog.executeMessengerCallback(iframe, options.callback);
                }
              }
              tries -= 1;
            }, 1000);
          } else if (params.method === 'share') {
            // https://developers.facebook.com/docs/sharing/reference/share-dialog
            _fb.ui(params, (response) => {
              // repsponse structure: https://developers.facebook.com/docs/sharing/reference/share-dialog#response
              // response usage at bottom: https://developers.facebook.com/docs/javascript/reference/FB.ui/
              if (trackFeedShare && response && !response.error_message) {
                const resource = ResourceFactory.create('LogFacebookFeedShare', {});
                resource.options = {
                  object_type: options.object_type,
                  object_id: options.object_id,
                  fb_ref: fbref,
                };
                resource.callCreate({ showError: false });
              }

              if (options.callback) {
                options.callback(response);
              }
            });
          }
        });
      }, user);
    }
  },

  _validParams: ['description', 'name', 'picture', 'to', 'href', 'caption', 'display'],

  /* Facebook Share type, defined at https://developers.facebook.com/docs/sharing/web */
  _validOptions: ['send', 'feed', 'share'],

  executeMessengerCallback(iframe, callback) {
    // NOTE(mwalker): hack: messenger iframe is currently resetting it's width to 0px after a certain timeout, so we need to set it back
    let tries = 10;
    const interval = setInterval(() => {
      const width = iframe.style.width;
      if (width === '0px' || !tries) {
        clearInterval(interval);
        iframe.style.width = '575px';
      }
      tries -= 1;
    }, 1000);

    if (callback) {
      callback();
    }
  },

  generateFbRef() {
    // fb_ref format is "pinterest_user_id:20 random hexadecimal characters".
    // This parameter is limited to 50 characters in length
    let refUniqueId = '';
    while (refUniqueId.length < FB_REF_UNIQUE_ID_LENGTH) {
      refUniqueId += Math.floor(Math.random() * 0xffff).toString(16);
    }
    return (user.id || context.instance.unauth_id) + ':' + refUniqueId;
  },

  _getDialogType(options) {
    const optionIsValidType = FacebookDialog._validOptions.includes(options.type);
    const type = optionIsValidType ? options.type : 'feed';
    return type;
  },

  getWindowOptions(width, height) {
    const windowWidth = width || FB_SHARER_WIDTH;
    const windowHeight = height || FB_SHARER_HEIGHT;
    const screenWidth = screen.width;
    const screenHeight = screen.height;
    const windowLeft = Math.round((screenWidth - windowWidth) / 2);
    const windowTop =
      screenHeight > windowHeight ? Math.round((screenHeight - windowHeight) / 2) : 0;

    return (
      'scrollbars=yes,resizable=yes,toolbar=no,location=yes,' +
      'width=' +
      windowWidth +
      ',height=' +
      windowHeight +
      ',' +
      'left=' +
      windowLeft +
      ',top=' +
      windowTop
    );
  },
};

export default FacebookDialog;
